import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { devolucionesParaCampana, mesDevueltoLabel } from '../utils/devolucionesCampana';
import { Bell, X, Check, AlertCircle, Mail, MessageCircle, Calendar, Clock, CheckCircle2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const NotificationBell = () => {
  const [notificaciones, setNotificaciones] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  // Salto breve ("un par de veces") al entrar en una página nueva si hay pendientes.
  const location = useLocation();
  const [bounce, setBounce] = useState(false);
  const bounceTimer = useRef(null);
  const prevPendRef = useRef(0);
  const dropdownRef = useRef(null);
  const audioRef = useRef(null);
  const prevCountRef = useRef(0);

  const navigate = useNavigate();
  // --- Recibos devueltos (Ann 2026-08): la campana se pone ROJA si hay alguno ---
  const [devoluciones, setDevoluciones] = useState([]);
  // --- Recordatorios de cita (V1 manual) ---
  const [vista, setVista] = useState('citas'); // 'citas' | 'notificaciones'
  const [citas, setCitas] = useState([]);
  const [citasLoading, setCitasLoading] = useState(false);
  const [soloHoy, setSoloHoy] = useState(true);
  const [hoy, setHoy] = useState(null);
  const [accionando, setAccionando] = useState(null); // `${id}:${tipo}:${canal}`

  // Polling cada 30 segundos
  // Ann 12-sep-2026: sin sesión NO se pregunta. Antes seguía llamando cada 30 s
  // aunque el token ya no valiera → 401 sin fin contra producción (y ruido que
  // tapaba lo importante). Si no hay token, ni se arranca ni se sigue.
  useEffect(() => {
    const haySesion = () => !!localStorage.getItem('token');
    const consultar = () => {
      if (!haySesion()) return;
      fetchNotifications();
      fetchCitas(); // para iluminar la campanita aunque no se abra
      fetchDevoluciones(); // recibos devueltos → campana roja
    };
    consultar();
    const interval = setInterval(consultar, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, []);

  const fetchDevoluciones = async () => {
    try {
      // La ruta /pendientes NUNCA existió: caía en /{devolucion_id} y daba 404 en
      // silencio (96 fallos en 3 h). El listado real ya devuelve {items:[...]}.
      const res = await axios.get(`${API}/gestor-devoluciones`);
      // REGLA DE ANN (14-sep-2026): la campana roja solo avisa de DEUDAS
      // RECUPERABLES PENDIENTES de clientas DE ALTA. La pantalla de Recibos
      // Devueltos sigue enseñándolo todo; aquí no, porque esto es un aviso
      // de trabajo por hacer, no un informe.
      setDevoluciones(devolucionesParaCampana(res.data.items || []));
    } catch (error) {
      // silencioso: si el usuario no tiene acceso a devoluciones, no molesta
    }
  };

  const abrirDevoluciones = () => {
    setShowDropdown(false);
    navigate('/admin?tab=contable_agrupada&subtab=devoluciones');
  };

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const [notifsRes, countRes] = await Promise.all([
        axios.get(`${API}/notificaciones?solo_no_leidas=false`),
        axios.get(`${API}/notificaciones/no-leidas/count`)
      ]);

      setNotificaciones(notifsRes.data);
      const newCount = countRes.data.count;
      
      // Si hay nuevas notificaciones, vibrar y sonar
      if (newCount > prevCountRef.current && prevCountRef.current > 0) {
        setShake(true);
        playNotificationSound();
        setTimeout(() => setShake(false), 500);
      }
      
      setNoLeidas(newCount);
      prevCountRef.current = newCount;
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const playNotificationSound = () => {
    // Crear audio si no existe
    if (!audioRef.current) {
      audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSyAzvLZiTYIGGS57OmxXxkFPpPX8Mp4LAgihcrxz3YoBC+m7+uWMggHFnLK89d9LQQZdr3mz0EXBDOSxevCayIGHCaM2+K0dCEEJFSK0+rADAAMTJPcwYQVACiHxN+gXxMGISFwsNjbHAAUQHW17MRlGgYchqvS1FgQABQ6jePUs2ccBiU7n+W5aSUEF2+k49tdGQUcUJTj0mwaBS1VjNrgnBwFLkyy7aJnHAQjYJXY1WAOCBWPvemUTg0FJnq558g5CQQnbrnvlU0MAiJqtumrUg8IGXu98qxeCQUsVLDvmF0NAy+Dx+7SeAwBIGnR+NBEDAQ=');
    }
    try {
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    } catch (e) {
      console.log('Audio error:', e);
    }
  };

  const handleMarcarLeida = async (notifId) => {
    try {
      await axios.put(`${API}/notificaciones/${notifId}/marcar-leida`);
      fetchNotifications();
    } catch (error) {
      toast.error('Error al marcar como leída');
    }
  };

  // ===== Recordatorios de cita =====
  // Peticiones de CAMBIO/CANCELACIÓN pedidas por la paciente (desde el portal o desde
  // el enlace del recordatorio). Encargo de Ann (22-jul-2026): la campanita pasa de
  // naranja VACÍA a naranja LLENA cuando hay alguna, para que no se pase por alto.
  const conCambioPedido = citas.filter((c) => c.confirmacion?.estado === 'reprogramar').length;

  const fetchCitas = async () => {
    setCitasLoading(true);
    try {
      // 30 días, no 5: con 5 una petición de cambio de una cita de dentro de tres
      // semanas NO aparecía en la campanita y podía no verla nadie a tiempo (lo vio
      // Ann el 22-jul-2026; ya pasaba con el enlace del WhatsApp). Los recordatorios
      // "de mañana" siguen contándose igual, esto solo amplía lo que se MIRA.
      const res = await axios.get(`${API}/citas/proximas?dias=30`);
      setCitas(res.data.citas || []);
      setHoy(res.data.hoy || null);
    } catch (error) {
      console.error('Error fetching citas:', error);
    } finally {
      setCitasLoading(false);
    }
  };

  const accionarAviso = async (cita, tipo, canal) => {
    const key = `${cita.id}:${tipo}:${canal}`;
    setAccionando(key);
    try {
      const res = await axios.post(`${API}/citas/${cita.id}/aviso`, { tipo, canal });
      if (canal === 'whatsapp' && res.data.wa_url) {
        window.open(res.data.wa_url, '_blank'); // abre WhatsApp con el texto prerelleno
      } else if (canal === 'email') {
        toast.success(tipo === 'aviso_reprogramacion' ? 'Reprogramación enviada por email' : 'Recordatorio enviado por email');
      }
      await fetchCitas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo enviar el aviso');
    } finally {
      setAccionando(null);
    }
  };

  // 'YYYY-MM-DD' -> 'DD/MM'
  const fechaCorta = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}`;
  };

  // Aviso sugerido según días hasta la cita (solo informativo, resalta el botón).
  const avisoSugerido = (cita) => {
    if (!cita.fecha || !hoy) return null;
    const diff = Math.round((new Date(cita.fecha) - new Date(hoy)) / 86400000);
    if (diff <= 1) return 'aviso_24h';     // el día anterior toca el recordatorio
    return 'aviso_alta';                    // antes, el mensaje informativo de alta
  };

  // Ann 2026-10-20: «cuando se reprograma una cita ya avisada, ya no aparece la posibilidad
  // de enviar el mensaje con el nuevo cambio». El cambio SÍ se registraba y la campanita se
  // iluminaba, pero con «Solo hoy» (que es lo que sale por defecto) la cita cambiada de otro
  // día NO se listaba: la gestión pendiente quedaba invisible. Una cita cambiada tras avisar,
  // o con cambio pedido por la paciente, se ve SIEMPRE, esté o no en «Solo hoy».
  const esGestionPendiente = (c) => !!c.reprogramacion?.pendiente_aviso || c.confirmacion?.estado === 'reprogramar';
  const citasVisibles = soloHoy ? citas.filter((c) => c.fecha === hoy || esGestionPendiente(c)) : citas;

  // La hora siempre se muestra con 'h' al lado (p. ej. 18:00h).
  const horaH = (h) => (h ? (String(h).endsWith('h') ? h : `${h}h`) : '');

  // Gestión pendiente que ILUMINA la campanita: recordatorios de MAÑANA (24h) sin enviar.
  const manana = hoy
    ? new Date(new Date(hoy).getTime() + 86400000).toISOString().slice(0, 10)
    : null;
  const pendientesManana = citas.filter(
    (c) => c.fecha === manana && !c.avisos?.aviso_24h?.enviado
  );
  // Citas que CAMBIARON de día/hora después de avisar y aún no se reenvió el aviso
  // (Ann 2026-09-07): también iluminan la campanita.
  const pendientesReprogramacion = citas.filter((c) => c.reprogramacion?.pendiente_aviso);
  const numPendientes = pendientesManana.length + pendientesReprogramacion.length;

  // Hace botar la campanita un par de veces (~1,7s) y luego se queda quieta.
  const triggerBounce = () => {
    setBounce(true);
    if (bounceTimer.current) clearTimeout(bounceTimer.current);
    bounceTimer.current = setTimeout(() => setBounce(false), 1700);
  };
  // Al ENTRAR en una página nueva (cualquier navegación), si hay pendientes → salta.
  useEffect(() => {
    if (numPendientes > 0) triggerBounce();
    // eslint-disable-next-line
  }, [location.key]);
  // Cuando aparece un pendiente nuevo (incluye la primera carga 0→N) → salta.
  useEffect(() => {
    if (numPendientes > 0 && prevPendRef.current === 0) triggerBounce();
    prevPendRef.current = numPendientes;
    // eslint-disable-next-line
  }, [numPendientes]);

  // Fila de un aviso (5d / 24h): azul = pendiente de enviar, verde = enviado.
  const renderAvisoRow = (cita, tipo, label, sugerido) => {
    const aviso = cita.avisos?.[tipo] || {};
    const enviado = aviso.enviado;
    const esSugerido = sugerido === tipo && !enviado;
    const keyEmail = `${cita.id}:${tipo}:email`;
    const keyWa = `${cita.id}:${tipo}:whatsapp`;
    const canalIcon = aviso.canal === 'whatsapp'
      ? <MessageCircle className="w-3 h-3" />
      : <Mail className="w-3 h-3" />;
    return (
      <div className={`flex items-center gap-2 ${esSugerido ? 'bg-blue-50 -mx-1 px-1 rounded' : ''}`}>
        <span className="text-xs text-gray-500 w-16 flex-shrink-0">{label}</span>
        {enviado ? (
          <span className="flex items-center gap-1 text-xs font-medium text-green-700">
            <CheckCircle2 className="w-3.5 h-3.5" /> Enviado {canalIcon}
          </span>
        ) : (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => accionarAviso(cita, tipo, 'email')}
              disabled={!cita.tiene_email || accionando === keyEmail}
              title={cita.tiene_email ? 'Enviar recordatorio por email' : 'El cliente no tiene email'}
              className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Mail className="w-3 h-3" /> Email
            </button>
            <button
              onClick={() => accionarAviso(cita, tipo, 'whatsapp')}
              disabled={!cita.tiene_telefono || accionando === keyWa}
              title={cita.tiene_telefono ? 'Abrir WhatsApp con el mensaje ya escrito' : 'El cliente no tiene un teléfono válido'}
              className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border border-green-200 text-green-700 hover:bg-green-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <MessageCircle className="w-3 h-3" /> WhatsApp
            </button>
            {esSugerido && <span className="text-[10px] text-blue-600 font-semibold whitespace-nowrap">⬅ hoy</span>}
          </div>
        )}
      </div>
    );
  };

  const getPriorityColor = (prioridad) => {
    switch (prioridad) {
      case 'urgente': return 'bg-red-500';
      case 'alta': return 'bg-orange-500';
      case 'normal': return 'bg-blue-500';
      case 'baja': return 'bg-gray-400';
      default: return 'bg-blue-500';
    }
  };

  const getPriorityIcon = (prioridad) => {
    if (prioridad === 'urgente' || prioridad === 'alta') {
      return <AlertCircle className="w-4 h-4" />;
    }
    return <Bell className="w-4 h-4" />;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Campanita */}
      <button
        onClick={() => {
          setShowDropdown(!showDropdown);
          if (!showDropdown) {
            setLoading(true);
            fetchNotifications().finally(() => setLoading(false));
            fetchCitas();
          }
        }}
        className={`relative p-2 rounded-full hover:bg-gray-100 transition-all ${
          shake || bounce ? 'animate-bounce' : ''
        }`}
        title={conCambioPedido > 0
          ? `${conCambioPedido} paciente${conCambioPedido !== 1 ? 's han' : ' ha'} pedido cambiar o anular su cita`
          : (numPendientes > 0
              ? `${numPendientes} recordatorio${numPendientes !== 1 ? 's' : ''} de mañana por enviar`
              : 'Notificaciones')}
      >
        <Bell
          fill={(devoluciones.length > 0 || conCambioPedido > 0) ? 'currentColor' : 'none'}
          className={`w-6 h-6 ${
            devoluciones.length > 0 ? 'text-red-600'
              : (numPendientes > 0 || conCambioPedido > 0) ? 'text-orange-500'
                : 'text-gray-700'
          } ${shake ? 'text-orange-600' : ''}`} />
        {devoluciones.length > 0 ? (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full animate-pulse">
            {devoluciones.length > 99 ? '99+' : devoluciones.length}
          </span>
        ) : numPendientes > 0 ? (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-orange-500 rounded-full">
            {numPendientes > 99 ? '99+' : numPendientes}
          </span>
        ) : noLeidas > 0 ? (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full animate-pulse">
            {noLeidas > 99 ? '99+' : noLeidas}
          </span>
        ) : null}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-2xl border-2 border-gray-200 z-50 max-h-[600px] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="p-4 pb-2 flex items-center justify-between">
              <h3 className="font-bold text-lg text-gray-900">Avisos</h3>
              <button
                onClick={() => setShowDropdown(false)}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            {/* Pestañas */}
            <div className="flex gap-1 px-3">
              <button
                onClick={() => setVista('citas')}
                className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-t-md transition-colors ${
                  vista === 'citas'
                    ? 'bg-white text-blue-700 border-x border-t border-gray-200'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Calendar className="w-4 h-4" /> Citas próximas
              </button>
              <button
                onClick={() => setVista('notificaciones')}
                className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-t-md transition-colors ${
                  vista === 'notificaciones'
                    ? 'bg-white text-blue-700 border-x border-t border-gray-200'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Bell className="w-4 h-4" /> Notificaciones
                {(devoluciones.length + noLeidas) > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-600 text-white rounded-full">
                    {(devoluciones.length + noLeidas) > 99 ? '99+' : (devoluciones.length + noLeidas)}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* ===== Vista: Citas próximas ===== */}
          {vista === 'citas' && (
            <>
              <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-2">
                <button
                  onClick={() => setSoloHoy(true)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                    soloHoy ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  Hoy
                </button>
                <button
                  onClick={() => setSoloHoy(false)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                    !soloHoy ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  Próximas
                </button>
                <span className="ml-auto text-xs text-gray-400">{citasVisibles.length} cita{citasVisibles.length !== 1 ? 's' : ''}</span>
              </div>
              {numPendientes > 0 && (
                <button
                  onClick={() => setSoloHoy(false)}
                  title="Ver las citas de mañana"
                  className="w-full flex items-center gap-2 px-4 py-2 bg-orange-50 border-b border-orange-100 text-left hover:bg-orange-100 transition-colors"
                >
                  <span className="flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-orange-500 rounded-full flex-shrink-0">
                    {numPendientes > 99 ? '99+' : numPendientes}
                  </span>
                  <span className="text-xs font-medium text-orange-800">
                    recordatorio{numPendientes !== 1 ? 's' : ''} de mañana por enviar
                  </span>
                  <span className="ml-auto text-xs font-semibold text-orange-700">Ver →</span>
                </button>
              )}
              <div className="overflow-y-auto flex-1">
                {citasLoading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-sm text-gray-500 mt-2">Cargando citas...</p>
                  </div>
                ) : citasVisibles.length === 0 ? (
                  <div className="p-8 text-center">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      {soloHoy ? 'No hay citas para hoy' : 'No hay citas en los próximos 5 días'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {citasVisibles.map((cita) => {
                      const sugerido = avisoSugerido(cita);
                      const estadoConf = cita.confirmacion?.estado;
                      const confirmada = estadoConf === 'confirmada';
                      const reprogramar = estadoConf === 'reprogramar';
                      return (
                        <div key={cita.id} className="p-3 hover:bg-gray-50 transition-colors">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">{cita.paciente || 'Sin nombre'}</p>
                              <p className="text-xs text-gray-500">
                                {fechaCorta(cita.fecha)} · {horaH(cita.hora)}
                                {cita.profesional ? ` · ${cita.profesional}` : ''}
                              </p>
                            </div>
                            {reprogramar ? (
                              <span className="flex items-center gap-1 text-xs font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                                <AlertCircle className="w-3.5 h-3.5" /> Reprogramar
                              </span>
                            ) : confirmada ? (
                              <span className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Confirmada
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                                <Clock className="w-3.5 h-3.5" /> Pendiente
                              </span>
                            )}
                          </div>
                          {/* Dos avisos + reprogramación si la cita cambió tras avisar (Ann 2026-09-07) */}
                          <div className="mt-2 space-y-1.5">
                            {renderAvisoRow(cita, 'aviso_alta', 'Al crear', sugerido)}
                            {renderAvisoRow(cita, 'aviso_24h', '24 h', sugerido)}
                            {cita.reprogramacion && (
                              <div className={`rounded px-1.5 py-1 -mx-1 ${cita.reprogramacion.pendiente_aviso ? 'bg-amber-50 border border-amber-200' : ''}`} data-testid={`aviso-reprogramacion-${cita.id}`}>
                                <p className="text-[11px] text-amber-800 mb-1">
                                  <RotateCcw className="w-3 h-3 inline mr-1" />
                                  Cita cambiada · antes {fechaCorta(cita.reprogramacion.fecha_anterior)} {horaH(cita.reprogramacion.hora_anterior)}
                                  {cita.reprogramacion.pendiente_aviso ? ' · avisar del cambio' : ''}
                                </p>
                                {renderAvisoRow(cita, 'aviso_reprogramacion', 'Cambio', cita.reprogramacion.pendiente_aviso ? 'aviso_reprogramacion' : null)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ===== Vista: Notificaciones ===== */}
          {vista === 'notificaciones' && (
          <>
          <div className="overflow-y-auto flex-1">
            {/* Recibos devueltos (Ann 2026-08): cliente + importe · clic en el nombre → listado */}
            {devoluciones.length > 0 && (
              <div className="border-b-2 border-red-200 bg-red-50">
                <div className="px-4 py-2 flex items-center gap-2">
                  <RotateCcw className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-bold text-red-700">Recibos devueltos ({devoluciones.length})</span>
                </div>
                <div className="divide-y divide-red-100">
                  {devoluciones.map((d) => (
                    <div key={d.devolucion_id} className="px-4 py-2 flex items-center justify-between gap-2 hover:bg-red-100 transition-colors">
                      <button onClick={abrirDevoluciones} title="Ir a Recibos Devueltos"
                        className="text-sm font-semibold text-red-800 underline decoration-red-300 hover:decoration-red-600 text-left">
                        {d.cliente_nombre || 'Sin nombre'}
                      </button>
                      {mesDevueltoLabel(d) && (
                        <span className="text-xs text-red-600 whitespace-nowrap" title="Mes del recibo devuelto">
                          {mesDevueltoLabel(d)}
                        </span>
                      )}
                      <span className="text-sm font-bold text-red-700 whitespace-nowrap">
                        {Number(d.importe || 0).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €
                      </span>
                    </div>
                  ))}
                </div>
                <button onClick={abrirDevoluciones} className="w-full text-center text-xs text-red-600 hover:text-red-800 py-2 font-semibold">
                  Ver todos en Recibos Devueltos →
                </button>
              </div>
            )}
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Cargando...</p>
              </div>
            ) : notificaciones.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No hay notificaciones</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notificaciones.map((notif) => (
                  <div
                    key={notif.id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${
                      !notif.leida_por_usuario ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icono de prioridad */}
                      <div className={`p-2 rounded-full ${getPriorityColor(notif.prioridad)} text-white flex-shrink-0`}>
                        {getPriorityIcon(notif.prioridad)}
                      </div>

                      {/* Contenido */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 mb-1">{notif.mensaje}</p>
                        
                        {/* Info de lecturas (para editores) */}
                        {notif.lecturas.length > 0 && notif.total_destinatarios > 1 && (
                          <div className="text-xs text-gray-500 mb-2">
                            Leído por: {notif.lecturas.map(l => l.user_email.split('@')[0]).join(', ')}
                            {notif.total_lecturas < notif.total_destinatarios && (
                              <span className="text-orange-600 ml-1">
                                ({notif.total_destinatarios - notif.total_lecturas} pendiente{notif.total_destinatarios - notif.total_lecturas !== 1 ? 's' : ''})
                              </span>
                            )}
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-400">
                            {new Date(notif.fecha_creacion).toLocaleString('es-ES', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>

                          {!notif.leida_por_usuario && (
                            <button
                              onClick={() => handleMarcarLeida(notif.id)}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                            >
                              <Check className="w-3 h-3" />
                              Marcar leída
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notificaciones.length > 0 && (
            <div className="p-3 border-t border-gray-200 bg-gray-50 text-center">
              <button
                onClick={() => {
                  // TODO: Navegar a página de todas las notificaciones
                  toast.info('Próximamente: Ver todas las notificaciones');
                }}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Ver todas
              </button>
            </div>
          )}
          </>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
